import React, { useEffect, useMemo, useState } from 'react';
import { DatePickerInput, DropdownMenu, Modal, Notification, SparkIcon, SparkModalPortal } from '@spark';
import { DEFAULT_WINDOW, combine, scheduleBounds, slotsFor, tomorrow } from '../utils/schedule';

const TIME_PLACEHOLDER = 'Select a time that best avoids user activity';
const NO_TIMES = 'No times left on this day. Pick another day.';
const TIP = 'Tip: Schedule during off-peak hours (e.g., 2:00 AM - 4:00 AM) to minimize user impact.';

/**
 * Schedule Platform Update (Figma 91:15173). Days and half-hour slots are in the
 * browser's time zone, limited to the upgrader's ``scheduleWindow`` (10 minutes
 * to 7 days ahead by default); the upgrader stores the time in UTC.
 */
export default function ScheduleUpdateModal({ open, scheduleWindow, onClose, onSchedule }) {
    const minLead = scheduleWindow?.min_lead_minutes ?? DEFAULT_WINDOW.min_lead_minutes;
    const days = scheduleWindow?.max_days ?? DEFAULT_WINDOW.max_days;
    const [bounds, setBounds] = useState(() => scheduleBounds({ min_lead_minutes: minLead, max_days: days }));
    const [day, setDay] = useState(() => tomorrow());
    const [slot, setSlot] = useState(null);
    const [slotsOpen, setSlotsOpen] = useState(false);

    useEffect(() => {
        if (open) {
            setBounds(scheduleBounds({ min_lead_minutes: minLead, max_days: days }));
            setDay(tomorrow());
            setSlot(null);
        }
    }, [open, minLead, days]);

    const slots = useMemo(() => slotsFor(day, bounds), [day, bounds]);
    const selected = slots.find((option) => option.value === slot) || null;

    return (
        <SparkModalPortal open={open} onClose={onClose} width={600}>
            <Modal
                open
                title="Schedule Platform Update"
                subtitle="Choose when you’d like the update to run."
                showSubtitle
                showOverlay={false}
                showScrollBar={false}
                fixedHeight={false}
                primaryLabel="Schedule update"
                primaryDisabled={!selected}
                tertiaryLabel="Cancel"
                onPrimaryClick={() => onSchedule(combine(day, selected.value))}
                onTertiaryClick={onClose}
                onCloseClick={onClose}
            >
                <div className="platform-upgrader-schedule">
                    <DatePickerInput
                        label="Select a date"
                        value={day}
                        onChange={setDay}
                        minDate={bounds.earliest}
                        maxDate={bounds.latest}
                    />
                    <DropdownMenu
                        selectType="single-select"
                        label="Time"
                        size="medium"
                        fill
                        placeholder={slots.length ? TIME_PLACEHOLDER : NO_TIMES}
                        text={selected?.label}
                        disabled={!slots.length}
                        showPrefixIcon
                        prefixIcon={<SparkIcon slug="clock-hour-icon" size="small" />}
                        open={slotsOpen}
                        onOpenChange={setSlotsOpen}
                        items={slots.map((option) => ({
                            label: option.label,
                            state: option.value === selected?.value ? 'selected' : 'default',
                            onClick: () => {
                                setSlot(option.value);
                                setSlotsOpen(false);
                            },
                        }))}
                    />
                    <Notification
                        feedback="info"
                        fill
                        elevated={false}
                        showTitle={false}
                        content={TIP}
                        showButton={false}
                        showCloseButton={false}
                    />
                </div>
            </Modal>
        </SparkModalPortal>
    );
}
